import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { GripVertical, Eye, EyeOff, Settings } from 'lucide-react';

export const DraggableWidget = ({ id, title, children, isVisible, onToggleVisibility }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: isDragging ? 'grabbing' : 'grab',
  };

  if (!isVisible) return null;

  return (
    <div ref={setNodeRef} style={style}>
      <Card className={`glass card-hover h-full ${isDragging ? 'ring-2 ring-primary' : ''}`}>
        <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
          <div className="flex items-center gap-2">
            <button 
              {...attributes} 
              {...listeners}
              className="cursor-grab hover:bg-gray-700 p-1 rounded transition-colors"
              aria-label="Drag to reorder"
            >
              <GripVertical className="w-4 h-4 text-gray-500" />
            </button>
            <CardTitle className="text-sm font-medium text-white">{title}</CardTitle>
          </div>
          {onToggleVisibility && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0"
              onClick={() => onToggleVisibility(id)}
            >
              <Eye className="w-3.5 h-3.5 text-gray-400" />
            </Button>
          )}
        </CardHeader>
        <CardContent className="pt-0">{children}</CardContent>
      </Card>
    </div>
  );
};

export const WidgetSettingsPanel = ({ widgets, onToggle, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-gray-900 border-gray-700">
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-white">Dashboard Widgets</CardTitle>
          <Button variant="ghost" size="sm" onClick={onClose}>×</Button>
        </CardHeader>
        <CardContent className="space-y-2">
          <p className="text-sm text-gray-400 mb-4">
            Toggle widgets on/off and drag to reorder on the dashboard.
          </p>
          {widgets.map((widget) => (
            <div
              key={widget.id}
              className="flex items-center justify-between p-3 bg-gray-800 rounded-lg"
            >
              <div className="flex items-center gap-3">
                <GripVertical className="w-4 h-4 text-gray-500" />
                <span className="text-white text-sm">{widget.title}</span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onToggle(widget.id)}
                className={widget.visible ? 'text-cyan-400' : 'text-gray-500'}
              >
                {widget.visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              </Button>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
};

export default DraggableWidget;
